import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ApiURL from '../../controllers/Api';
import { toast } from '../../utils/toast';
import TopCompanies from './TopCompanies';

gsap.registerPlugin(ScrollTrigger);

/* ------------------------------------------------------------------ */
/*  Brand tokens                                                       */
/* ------------------------------------------------------------------ */
const INK = '#0B1020';
const INDIGO = '#5B47E0';
const CORAL = '#FF6B57';
const EMERALD = '#10B981';

/* ------------------------------------------------------------------ */
/*  Icons (inline, stroke-based, 1.6 weight — no external dependency)  */
/* ------------------------------------------------------------------ */
const Ic = ({ d, size = 16, className = '', fill = 'none', strokeWidth = 1.7, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {d ? <path d={d} /> : children}
  </svg>
);

const SearchIcon = (p) => <Ic {...p}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></Ic>;
const PinIcon = (p) => <Ic {...p}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="2.6" /></Ic>;
const BagIcon = (p) => <Ic {...p}><rect x="3" y="7" width="18" height="13" rx="2.5" /><path d="M8 7V5.5A2.5 2.5 0 0 1 10.5 3h3A2.5 2.5 0 0 1 16 5.5V7" /><path d="M3 12h18" /></Ic>;
const LevelIcon = (p) => <Ic {...p}><path d="M5 20V11" /><path d="M12 20V4" /><path d="M19 20v-6" /></Ic>;
const CashIcon = (p) => <Ic {...p}><path d="M12 2v20" /><path d="M17 6.5C17 4.6 14.8 3.5 12 3.5S7 4.6 7 6.5 9.2 9.5 12 9.5s5 1.1 5 3-2.2 3-5 3-5-1.1-5-3" /></Ic>;
const BookmarkIcon = (p) => <Ic {...p}><path d="M6 4.5A1.5 1.5 0 0 1 7.5 3h9A1.5 1.5 0 0 1 18 4.5V21l-6-4-6 4Z" /></Ic>;
const ShareIcon = (p) => <Ic {...p}><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5 8.6 10.5" /></Ic>;
const SparkIcon = (p) => <Ic {...p} fill="currentColor" strokeWidth="0"><path d="M12 2.5 13.9 8 19.5 9.4 15.3 13.2 16.4 19 12 15.8 7.6 19l1.1-5.8L4.5 9.4 10.1 8Z" /></Ic>;
const UsersIcon = (p) => <Ic {...p}><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M16 5.2a3.2 3.2 0 0 1 0 5.6" /><path d="M17.5 14.5A5.5 5.5 0 0 1 20.5 20" /></Ic>;
const CalIcon = (p) => <Ic {...p}><rect x="3.5" y="5" width="17" height="16" rx="2.5" /><path d="M3.5 9.5h17M8 3v4M16 3v4" /></Ic>;
const ClockIcon = (p) => <Ic {...p}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></Ic>;
const UploadIcon = (p) => <Ic {...p}><path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" /><path d="M4 16v2.5A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5V16" /></Ic>;
const CheckIcon = (p) => <Ic {...p}><path d="m4.5 12.5 5 5 10-11" /></Ic>;
const ArrowIcon = (p) => <Ic {...p}><path d="M5 12h14m0 0-6-6m6 6-6 6" /></Ic>;
const BoltIcon = (p) => <Ic {...p} fill="currentColor" strokeWidth="0"><path d="M13 2 4 14h6l-1 8 9-12h-6Z" /></Ic>;
const BuildIcon = (p) => <Ic {...p}><path d="M4 21V5.5A1.5 1.5 0 0 1 5.5 4h7A1.5 1.5 0 0 1 14 5.5V21" /><path d="M14 9h4.5A1.5 1.5 0 0 1 20 10.5V21" /><path d="M2.5 21h19M7 8h3M7 12h3M7 16h3M17 13h0M17 17h0" /></Ic>;

/* ------------------------------------------------------------------ */
/*  Small helpers                                                      */
/* ------------------------------------------------------------------ */
const useCountUp = (target, duration = 900) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setVal(target);
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

const Stat = ({ icon: Icon, value, label, dark }) => {
  const n = useCountUp(value);
  return (
    <div
      className={`flex flex-col gap-0.5 rounded-xl p-2.5 sm:gap-1 sm:rounded-2xl sm:p-3.5 ${
        dark ? 'bg-[#0B1020] text-white' : 'bg-white ring-1 ring-slate-200/80'
      }`}
    >
      <Icon size={15} className={dark ? 'text-indigo-300' : 'text-indigo-500'} />
      <div className={`text-base font-semibold tabular-nums sm:text-xl ${dark ? 'text-white' : 'text-slate-900'}`}>{n}</div>
      <div className={`text-[10.5px] font-medium sm:text-[11px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</div>
    </div>
  );
};

/* Circular match-score ring — the signature element (gradient + glow) */
const ScoreRing = ({ score = 0 }) => {
  const animated = useCountUp(score, 1100);
  const gid = useRef(`g${Math.random().toString(36).slice(2, 9)}`).current;
  const R = 26;
  const C = 2 * Math.PI * R;
  return (
    <div className="relative grid h-14 w-14 shrink-0 place-items-center sm:h-[72px] sm:w-[72px]">
      <svg viewBox="0 0 64 64" className="h-14 w-14 -rotate-90 sm:h-[72px] sm:w-[72px]">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={INDIGO} />
            <stop offset="55%" stopColor="#7C5CE0" />
            <stop offset="100%" stopColor={EMERALD} />
          </linearGradient>
        </defs>
        <circle cx="32" cy="32" r={R} fill="none" stroke="#EEF0F6" strokeWidth="6" />
        <motion.circle
          cx="32"
          cy="32"
          r={R}
          fill="none"
          stroke={`url(#${gid})`}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={C}
          style={{ filter: 'drop-shadow(0 1px 3px rgba(91,71,224,0.45))' }}
          initial={{ strokeDashoffset: C }}
          whileInView={{ strokeDashoffset: C - (C * Math.min(score, 100)) / 100 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-[13px] font-bold tabular-nums text-slate-900 sm:text-[15px]">{animated}%</span>
        <span className="text-[7.5px] font-semibold uppercase tracking-[0.12em] text-slate-400 sm:text-[8px]">match</span>
      </div>
    </div>
  );
};

/* ================================================================== */
/*  ALL ORIGINAL LOGIC BELOW IS UNCHANGED                              */
/* ================================================================== */
const emptyFilters = {
  search: '',
  location: '',
  type: '',
  experience: '',
  salary: '',
  datePosted: '',
  remote: false,
  featured: false,
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeJob = (job) => ({
  ...job,
  tags: Array.isArray(job.tags) ? job.tags : Array.isArray(job.skills) ? job.skills : [],
  company_logo: job.company_logo || null,
  salary_min: job.salary_min || null,
  salary_max: job.salary_max || null,
  match_score: Number(job.match_score || 0),
  applicant_count: Number(job.applicant_count || 0),
  is_remote: Boolean(job.is_remote),
  is_featured: Boolean(job.is_featured),
  experience_level: job.experience_level || 'Not specified',
  job_type: job.job_type || 'Full-time',
  posted_at: job.posted_at || new Date().toISOString(),
  description: job.description || '',
  requirements: job.requirements || '',
  location: job.location || 'Remote',
  company_name: job.company_name || 'Unknown Company',
  title: job.title || 'Untitled Position',
});

const JobFeed = ({ isLoggedIn = false, userData = null }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState(new Set());
  const [appliedJobs, setAppliedJobs] = useState(new Set());
  const [filters, setFilters] = useState(emptyFilters);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [resumeProfile, setResumeProfile] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);

  const rootRef = useRef(null);
  const heroRef = useRef(null);
  const resultHeadRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setSavedJobs(new Set());
      setAppliedJobs(new Set());
      setResumeProfile(null);
      return;
    }

    fetchSavedJobs();
    fetchAppliedJobs();
    fetchCurrentResume();
  }, [isLoggedIn]);

  /* GSAP intro + scroll reveal (UI only) */
  useEffect(() => {
    if (loading) return;
    const ctx = gsap.context(() => {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      gsap.from(heroRef.current?.querySelectorAll('[data-hero]') || [], {
        y: 24,
        opacity: 0,
        duration: 0.7,
        ease: 'power3.out',
        stagger: 0.08,
      });

      if (resultHeadRef.current) {
        gsap.from(resultHeadRef.current, {
          opacity: 0,
          y: 20,
          duration: 0.6,
          ease: 'power3.out',
          scrollTrigger: { trigger: resultHeadRef.current, start: 'top 88%' },
        });
      }
    }, rootRef);
    return () => ctx.revert();
  }, [loading]);

  /* GSAP scroll-reveal for the job cards (re-binds when the list changes) */
  useEffect(() => {
    if (loading || !listRef.current) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray('.jf-card');
      if (!cards.length) return;
      gsap.set(cards, { opacity: 0, y: 26 });
      ScrollTrigger.batch(cards, {
        start: 'top 92%',
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            stagger: 0.09,
            ease: 'power3.out',
            clearProps: 'transform',
            overwrite: true,
          }),
      });
      ScrollTrigger.refresh();
    }, listRef);

    return () => ctx.revert();
  }, [filteredJobsKey(filters, jobs), showSavedOnly, loading]); // eslint-disable-line

  const filteredJobs = useMemo(() => {
    let filtered = [...jobs];

    if (showSavedOnly) {
      filtered = filtered.filter((job) => savedJobs.has(job.id));
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter((job) =>
        [job.title, job.company_name, job.description, ...(job.tags || [])]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(searchTerm))
      );
    }

    if (filters.location) {
      const locationTerm = filters.location.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          String(job.location || '').toLowerCase().includes(locationTerm) ||
          (locationTerm === 'remote' && job.is_remote)
      );
    }

    if (filters.type) {
      filtered = filtered.filter((job) => job.job_type === filters.type);
    }

    if (filters.experience) {
      filtered = filtered.filter((job) => job.experience_level === filters.experience);
    }

    if (filters.salary) {
      const min = Number(filters.salary.split('-')[0]?.replace(/[^0-9]/g, '') || 0);
      filtered = filtered.filter((job) => Number(job.salary_min || 0) >= min);
    }

    if (filters.remote) {
      filtered = filtered.filter((job) => job.is_remote);
    }

    if (filters.featured) {
      filtered = filtered.filter((job) => job.is_featured);
    }

    if (filters.datePosted) {
      const now = new Date();
      const cutoff = new Date();
      if (filters.datePosted === '24h') cutoff.setDate(now.getDate() - 1);
      if (filters.datePosted === '3d') cutoff.setDate(now.getDate() - 3);
      if (filters.datePosted === '1w') cutoff.setDate(now.getDate() - 7);
      if (filters.datePosted === '1m') cutoff.setMonth(now.getMonth() - 1);
      filtered = filtered.filter((job) => new Date(job.posted_at) >= cutoff);
    }

    return filtered;
  }, [filters, jobs, showSavedOnly, savedJobs]);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${ApiURL}jobs.php`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Failed to fetch jobs');
      }
      setJobs((result.data || []).map(normalizeJob));
    } catch (error) {
      toast.error(error.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedJobs = async () => {
    try {
      const response = await fetch(`${ApiURL}saved_jobs.php`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.success) {
        setSavedJobs(new Set((result.data || []).map((job) => job.job_id || job.id)));
      }
    } catch (error) {
      console.error('Error fetching saved jobs:', error);
    }
  };

  const fetchAppliedJobs = async () => {
    try {
      const response = await fetch(`${ApiURL}applications.php?status=all`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.success) {
        setAppliedJobs(new Set((result.data || []).map((app) => app.job_id)));
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  };

  const fetchCurrentResume = async () => {
    setResumeLoading(true);
    try {
      const storedUser = localStorage.getItem('user');
      const parsedUser = storedUser ? JSON.parse(storedUser) : null;
      if (!parsedUser?.id) {
        setResumeProfile(null);
        return;
      }

      const profileResponse = await fetch(`${ApiURL}get_profile.php?user_id=${parsedUser.id}`, {
        headers: getAuthHeaders(),
      });
      const profileResult = await profileResponse.json().catch(() => ({}));
      const resumes = profileResult?.data?.resumes || [];
      const currentResume = resumes.find((resume) => resume.is_current) || resumes[0];

      if (!currentResume?.id) {
        setResumeProfile(null);
        return;
      }

      const detailsResponse = await fetch(
        `${ApiURL}get_resume_details.php?resume_id=${currentResume.id}`,
        { headers: getAuthHeaders() }
      );
      const detailsResult = await detailsResponse.json().catch(() => ({}));
      if (detailsResponse.ok && detailsResult.success) {
        setResumeProfile(detailsResult.data);
      } else {
        setResumeProfile(currentResume);
      }
    } catch (error) {
      console.error('Error fetching resume profile:', error);
      setResumeProfile(null);
    } finally {
      setResumeLoading(false);
    }
  };

  const handleSaveJob = async (jobId) => {
    if (!isLoggedIn) {
      toast.warning('Please login to save jobs');
      navigate('/login');
      return;
    }

    try {
      const isCurrentlySaved = savedJobs.has(jobId);
      const response = await fetch(`${ApiURL}save_job.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          job_id: jobId,
          action: isCurrentlySaved ? 'unsave' : 'save',
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Failed to update saved jobs');
      }

      setSavedJobs((prev) => {
        const next = new Set(prev);
        if (isCurrentlySaved) {
          next.delete(jobId);
        } else {
          next.add(jobId);
        }
        return next;
      });
      toast.success(isCurrentlySaved ? 'Removed from saved jobs' : 'Saved job successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update saved jobs');
    }
  };

  const openApplyPanel = async (job) => {
    setSelectedJob(job);
    if (!isLoggedIn) {
      return;
    }
    await fetchCurrentResume();
  };

  const handleApplyJob = async () => {
    if (!selectedJob) {
      return;
    }

    if (!isLoggedIn) {
      toast.warning('Please login to apply');
      navigate('/login');
      return;
    }

    if (userData?.user_type && userData.user_type !== 'individual') {
      toast.warning('Only job seekers can apply to jobs');
      return;
    }

    if (!resumeProfile?.id) {
      toast.warning('Please upload your resume before applying');
      return;
    }

    if (appliedJobs.has(selectedJob.id)) {
      toast.warning('You already applied to this job');
      return;
    }

    setApplyLoading(true);
    try {
      const response = await fetch(`${ApiURL}apply_job.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ job_id: selectedJob.id }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Failed to apply');
      }

      setAppliedJobs((prev) => new Set(prev).add(selectedJob.id));
      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? { ...job, applicant_count: Number(job.applicant_count || 0) + 1 }
            : job
        )
      );
      toast.success(`Application submitted${result.data?.match_score ? ` with ${result.data.match_score}% match` : ''}`);
    } catch (error) {
      toast.error(error.message || 'Failed to apply');
    } finally {
      setApplyLoading(false);
    }
  };

  const handleResumeUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!isLoggedIn) {
      toast.warning('Please login to upload your resume');
      navigate('/login');
      return;
    }

    setUploadingResume(true);
    try {
      const formData = new FormData();
      formData.append('resume', file);

      const response = await fetch(`${ApiURL}upload_resume.php`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || 'Failed to upload resume');
      }

      toast.success('Resume uploaded successfully');
      await fetchCurrentResume();
    } catch (error) {
      toast.error(error.message || 'Failed to upload resume');
    } finally {
      setUploadingResume(false);
      event.target.value = '';
    }
  };

  const handleShareJob = async (job) => {
    const shareUrl = `${window.location.origin}/jobs`;
    const shareText = `${job.title} at ${job.company_name}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, text: shareText, url: shareUrl });
      } catch (error) {
        console.error('Error sharing:', error);
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(`${shareText} - ${shareUrl}`);
      toast.success('Job link copied');
    } catch (error) {
      toast.error('Unable to copy job link');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil(Math.abs(now - date) / (1000 * 60 * 60 * 24));
    if (diffDays <= 1) return 'Today';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatSalary = (min, max) => {
    const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
    if (!min && !max) return 'Salary not disclosed';
    if (!max) return `${inr(min)}+`;
    if (!min) return `Up to ${inr(max)}`;
    return `${inr(min)} - ${inr(max)}`;
  };

  /* cursor spotlight position writer (UI only) */
  const handleSpotlight = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  /* ---------------------------------------------------------------- */
  /*  UI                                                              */
  /* ---------------------------------------------------------------- */
  const chip = (active) =>
    `inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-all duration-200 ${
      active
        ? 'bg-[#0B1020] text-white shadow-sm shadow-indigo-900/20'
        : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300 hover:text-slate-900'
    }`;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F7F7FB]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative h-12 w-12">
            <div className="absolute inset-0 animate-spin rounded-full border-[3px] border-slate-200 border-t-[#5B47E0]" />
          </div>
          <p className="text-sm font-medium text-slate-500">Loading opportunities…</p>
        </div>
      </div>
    );
  }

  const metaItem = (Icon, text) => (
    <span className="inline-flex items-center gap-1.5">
      <Icon size={13} className="text-slate-400" />
      <span className="text-slate-600">{text}</span>
    </span>
  );

  const steps = [
    { icon: SearchIcon, title: 'Browse openly', text: 'Every role is visible without an account.' },
    { icon: BoltIcon, title: 'See your match', text: 'Each post is scored against your resume.' },
    { icon: ArrowIcon, title: 'Apply in one tap', text: 'Use your saved resume or upload a fresh one.' },
  ];

  return (
    <div ref={rootRef} className="min-h-screen bg-[#F7F7FB] text-slate-900 antialiased">
      <style>{`
        .jf-card { box-shadow: 0 0 0 1px rgba(15,23,42,.06), 0 10px 30px -18px rgba(11,16,32,.20); transition: transform .5s cubic-bezier(.22,1,.36,1), box-shadow .5s cubic-bezier(.22,1,.36,1); }
        .jf-card:hover { transform: translateY(-4px); box-shadow: 0 0 0 1px rgba(91,71,224,.25), 0 32px 60px -30px rgba(91,71,224,.42); }
        .jf-card.is-selected { box-shadow: 0 0 0 1.6px ${INDIGO}, 0 30px 60px -28px rgba(91,71,224,.45); }
        .jf-rail { background: #E3E6EF; transform: scaleY(.5); transform-origin: top; transition: transform .5s cubic-bezier(.22,1,.36,1), background .5s; }
        .jf-card:hover .jf-rail, .jf-card.is-selected .jf-rail { transform: scaleY(1); }
        .jf-card.is-featured .jf-rail { background: linear-gradient(180deg, ${CORAL}, #FF9C8E); }
        .jf-card.is-selected .jf-rail { background: linear-gradient(180deg, ${INDIGO}, ${EMERALD}); }
        .jf-spot { background: radial-gradient(440px circle at var(--mx,50%) var(--my,50%), rgba(91,71,224,.10), transparent 42%); opacity: 0; transition: opacity .45s ease; }
        .jf-card:hover .jf-spot { opacity: 1; }
        .jf-logo { transition: transform .5s cubic-bezier(.22,1,.36,1); }
        .jf-card:hover .jf-logo { transform: scale(1.06) rotate(-3deg); }
        .jf-arrow { transition: transform .35s cubic-bezier(.22,1,.36,1); }
        .jf-apply:hover .jf-arrow { transform: translateX(3px); }
        @keyframes jfBlobA { 0%,100% { transform: translate(0,0); } 50% { transform: translate(20px,-14px); } }
        @keyframes jfBlobB { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-16px,16px); } }
        @keyframes jfLine { to { transform: scaleY(1); } }
        .jf-blob-a { animation: jfBlobA 9s ease-in-out infinite; }
        .jf-blob-b { animation: jfBlobB 11s ease-in-out infinite; }
        .jf-stepline { transform: scaleY(0); transform-origin: top; animation: jfLine .85s .12s cubic-bezier(.22,1,.36,1) forwards; }
        @media (prefers-reduced-motion: reduce) {
          .jf-card:hover { transform: none; }
          .jf-card:hover .jf-logo { transform: none; }
          .jf-blob-a, .jf-blob-b, .jf-stepline { animation: none; }
          .jf-stepline { transform: scaleY(1); }
        }
      `}</style>

      {/* ---------------- HERO (unchanged) ---------------- */}
      <section className="relative overflow-hidden border-b border-slate-200/70 bg-white">
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.44]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?q=80&w=1176&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')",
            maskImage: 'linear-gradient(to bottom, black 0%, black 55%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 55%, transparent 100%)',
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.55]"
          style={{
            background:
              'radial-gradient(680px 320px at 88% -10%, rgba(91,71,224,0.10), transparent 60%), radial-gradient(560px 300px at -5% 0%, rgba(255,107,87,1), transparent 55%)',
          }}
        />
        <div ref={heroRef} className="relative mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-9 lg:px-8 lg:py-12">
          <div className="grid gap-8 lg:grid-cols-[1.35fr_0.65fr] lg:items-center">
            <div>
              <span
                data-hero
                className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-600 ring-1 ring-indigo-100"
              >
                <BoltIcon size={13} /> Browse, compare, and quick-apply in one flow
              </span>
              <h1
                data-hero
                className="mt-3 max-w-xl text-xl font-semibold leading-[1.15] tracking-tight text-slate-900 sm:mt-4 sm:text-[32px]"
              >
                Find your next role and apply the moment your profile is ready.
              </h1>
              <p data-hero className="mt-2.5 max-w-lg text-[13px] leading-relaxed text-slate-500 sm:mt-3 sm:text-sm">
                Explore openings publicly, line up company details side by side, and apply with your
                saved resume or upload a fresh one — without leaving the page.
              </p>
            </div>

            <div data-hero className="grid grid-cols-3 gap-2.5">
              <Stat icon={BagIcon} value={jobs.length} label="Open roles" dark />
              <Stat icon={BuildIcon} value={new Set(jobs.map((j) => j.company_name)).size} label="Companies" />
              <Stat icon={PinIcon} value={jobs.filter((j) => j.is_remote).length} label="Remote" />
            </div>
          </div>

          {/* Search bar */}
          <div data-hero className="mt-7 rounded-2xl bg-white p-2.5 shadow-[0_10px_40px_-12px_rgba(11,16,32,0.18)] ring-1 ring-slate-200/80">
            <div className="grid gap-2 sm:grid-cols-[1.25fr_1fr_auto]">
              <div className="flex items-center gap-2.5 rounded-xl bg-slate-50 px-3.5 ring-1 ring-transparent focus-within:bg-white focus-within:ring-slate-300">
                <SearchIcon size={17} className="shrink-0 text-slate-400" />
                <input
                  type="text"
                  placeholder="Role, company or skill"
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  className="w-full bg-transparent py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
              </div>
              <div className="flex items-center gap-2.5 rounded-xl bg-slate-50 px-3.5 ring-1 ring-transparent focus-within:bg-white focus-within:ring-slate-300">
                <PinIcon size={17} className="shrink-0 text-slate-400" />
                <input
                  type="text"
                  placeholder="Location or remote"
                  value={filters.location}
                  onChange={(e) => setFilters((prev) => ({ ...prev, location: e.target.value }))}
                  className="w-full bg-transparent py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400"
                />
              </div>
              <motion.button
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#5B47E0] px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-indigo-900/20 transition-colors hover:bg-[#4d3ad0]"
              >
                <SearchIcon size={16} /> Search
              </motion.button>
            </div>

            <div className="mt-2.5 flex flex-wrap gap-2 px-1 pb-1">
              <button onClick={() => setFilters((p) => ({ ...p, remote: !p.remote }))} className={chip(filters.remote)}>
                <PinIcon size={13} /> Remote
              </button>
              <button
                onClick={() => setFilters((p) => ({ ...p, type: p.type === 'Full-time' ? '' : 'Full-time' }))}
                className={chip(filters.type === 'Full-time')}
              >
                <BagIcon size={13} /> Full-time
              </button>
              <button
                onClick={() => setFilters((p) => ({ ...p, experience: p.experience === 'Senior' ? '' : 'Senior' }))}
                className={chip(filters.experience === 'Senior')}
              >
                <LevelIcon size={13} /> Senior
              </button>
              <button onClick={() => setFilters((p) => ({ ...p, featured: !p.featured }))} className={chip(filters.featured)}>
                <SparkIcon size={12} /> Featured
              </button>
              <button
                onClick={() => setFilters(emptyFilters)}
                className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium text-slate-400 transition-colors hover:text-slate-700"
              >
                Clear all
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- TOP COMPANIES + CATEGORIES ---------------- */}
      <TopCompanies
        jobs={jobs}
        onSelectCompany={(name) => {
          setFilters((prev) => ({ ...prev, search: name }));
          resultHeadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        onSelectKeyword={(keyword) => {
          setFilters((prev) => ({ ...prev, search: keyword }));
          resultHeadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />

      {/* ---------------- RESULTS (redesigned) ---------------- */}
      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div ref={resultHeadRef} className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-indigo-500">
              {showSavedOnly ? (
                <>
                  <BookmarkIcon size={13} fill="currentColor" /> Saved jobs
                </>
              ) : (
                <>
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  Live openings
                </>
              )}
            </div>
            <h2 className="mt-1.5 flex items-baseline gap-2 text-xl font-semibold text-slate-900">
              <span className="tabular-nums">{filteredJobs.length}</span>
              <span className="text-slate-400">
                {showSavedOnly
                  ? `${filteredJobs.length === 1 ? 'job' : 'jobs'} saved`
                  : `${filteredJobs.length === 1 ? 'role' : 'roles'} found`}
              </span>
            </h2>
          </div>

          {/* All / Saved segmented toggle */}
          <div className="inline-flex shrink-0 items-center gap-1 rounded-xl bg-white p-1 ring-1 ring-slate-200">
            <button
              onClick={() => setShowSavedOnly(false)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                !showSavedOnly ? 'bg-[#0B1020] text-white' : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <BagIcon size={14} /> All jobs
            </button>
            <button
              onClick={() => {
                if (!isLoggedIn) {
                  toast.warning('Please login to view saved jobs');
                  navigate('/login');
                  return;
                }
                setShowSavedOnly(true);
              }}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                showSavedOnly ? 'bg-[#0B1020] text-white' : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <BookmarkIcon size={14} fill={showSavedOnly ? 'currentColor' : 'none'} /> Saved
              {isLoggedIn && savedJobs.size > 0 && (
                <span
                  className={`ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[11px] font-bold tabular-nums ${
                    showSavedOnly ? 'bg-white/20 text-white' : 'bg-indigo-100 text-indigo-700'
                  }`}
                >
                  {savedJobs.size}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
          {/* Job list */}
          <div ref={listRef} className="space-y-4">
            {filteredJobs.length === 0 ? (
              showSavedOnly ? (
                <div className="rounded-2xl bg-white p-10 text-center shadow-[0_0_0_1px_rgba(15,23,42,.06),0_10px_30px_-18px_rgba(11,16,32,.20)]">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-amber-50 text-amber-500">
                    <BookmarkIcon size={20} />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-slate-900">No saved jobs yet</h3>
                  <p className="mt-1.5 text-[13px] text-slate-500">
                    Tap the <BookmarkIcon size={13} className="-mt-0.5 inline" /> bookmark on any job to save it here for later.
                  </p>
                  <button
                    onClick={() => setShowSavedOnly(false)}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#0B1020] px-4 py-2 text-[13px] font-semibold text-white"
                  >
                    Browse all jobs <ArrowIcon size={14} />
                  </button>
                </div>
              ) : (
                <div className="rounded-2xl bg-white p-10 text-center shadow-[0_0_0_1px_rgba(15,23,42,.06),0_10px_30px_-18px_rgba(11,16,32,.20)]">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-slate-100 text-slate-400">
                    <SearchIcon size={20} />
                  </div>
                  <h3 className="mt-4 text-base font-semibold text-slate-900">No matching roles</h3>
                  <p className="mt-1.5 text-[13px] text-slate-500">Widen your search or clear the filters to see more.</p>
                  <button
                    onClick={() => setFilters(emptyFilters)}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#0B1020] px-4 py-2 text-[13px] font-semibold text-white"
                  >
                    Clear filters <ArrowIcon size={14} />
                  </button>
                </div>
              )
            ) : (
              filteredJobs.map((job) => {
                const isSelected = selectedJob?.id === job.id;
                const isApplied = appliedJobs.has(job.id);
                const isSaved = savedJobs.has(job.id);
                return (
                  <article
                    key={job.id}
                    onClick={() => openApplyPanel(job)}
                    onMouseMove={handleSpotlight}
                    className={`jf-card group relative cursor-pointer overflow-hidden rounded-2xl bg-white p-3 sm:p-5 ${
                      isSelected ? 'is-selected' : ''
                    } ${job.is_featured ? 'is-featured' : ''}`}
                  >
                    <span className="jf-rail absolute inset-y-4 left-0 w-[3px] rounded-r-full sm:inset-y-5" />
                    <div className="jf-spot pointer-events-none absolute inset-0" />

                    <div className="relative z-10">
                      <div className="flex gap-3 sm:gap-4">
                        <div className="relative shrink-0">
                          <img
                            src={
                              job.company_logo ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                job.company_name
                              )}&background=5B47E0&color=fff&size=128&bold=true`
                            }
                            alt={job.company_name}
                            className="jf-logo h-10 w-10 rounded-xl object-cover ring-1 ring-slate-200 sm:h-12 sm:w-12"
                          />
                          {job.is_featured && (
                            <span className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-[#FF6B57] text-white ring-2 ring-white">
                              <SparkIcon size={10} />
                            </span>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="truncate text-[14px] font-semibold text-slate-900 transition-colors group-hover:text-[#5B47E0] sm:text-[15px]">
                                {job.title}
                              </h3>
                              <p className="mt-0.5 truncate text-[12px] font-medium text-slate-500 sm:text-[13px]">
                                {job.company_name}
                              </p>
                            </div>
                            <ScoreRing score={job.match_score} />
                          </div>

                          {/* de-boxed meta row: inline text + middots, one salary pill */}
                          <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[11.5px] sm:mt-3 sm:gap-x-3 sm:text-[12.5px]">
                            {metaItem(PinIcon, job.location)}
                            <span className="h-1 w-1 rounded-full bg-slate-300" />
                            {metaItem(BagIcon, job.job_type)}
                            <span className="h-1 w-1 rounded-full bg-slate-300" />
                            {metaItem(LevelIcon, job.experience_level)}
                            <span className="ml-0.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                              <CashIcon size={13} />
                              {formatSalary(job.salary_min, job.salary_max)}
                            </span>
                          </div>

                          <p className="mt-2 line-clamp-2 text-[12px] leading-relaxed text-slate-500 sm:mt-3 sm:text-[13px]">
                            {job.description}
                          </p>

                          {!!job.tags?.length && (
                            <div className="mt-2 flex flex-wrap gap-1.5 sm:mt-3">
                              {job.tags.slice(0, 5).map((tag) => (
                                <span
                                  key={tag}
                                  className="rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-600"
                                >
                                  {tag}
                                </span>
                              ))}
                              {job.tags.length > 5 && (
                                <span className="rounded-md px-1.5 py-0.5 text-[11px] font-medium text-slate-400">
                                  +{job.tags.length - 5}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3 sm:mt-4 sm:gap-3 sm:pt-3.5">
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400 sm:gap-3 sm:text-[12px]">
                          <span className="inline-flex items-center gap-1.5"><ClockIcon size={13} /> {formatDate(job.posted_at)}</span>
                          <span className="inline-flex items-center gap-1.5"><UsersIcon size={13} /> {job.applicant_count} applied</span>
                          {job.deadline && (
                            <span className="inline-flex items-center gap-1.5"><CalIcon size={13} /> by {new Date(job.deadline).toLocaleDateString()}</span>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleSaveJob(job.id)}
                            aria-label={isSaved ? 'Saved' : 'Save job'}
                            className={`grid h-8 w-8 place-items-center rounded-lg transition-colors ${
                              isSaved
                                ? 'bg-amber-50 text-amber-500'
                                : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                            }`}
                          >
                            <BookmarkIcon size={16} fill={isSaved ? 'currentColor' : 'none'} />
                          </button>
                          <button
                            onClick={() => handleShareJob(job)}
                            aria-label="Share job"
                            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                          >
                            <ShareIcon size={15} />
                          </button>
                          <button
                            onClick={() => openApplyPanel(job)}
                            className={`jf-apply inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                              isApplied
                                ? 'bg-emerald-50 text-emerald-600'
                                : 'bg-[#0B1020] text-white hover:bg-[#191f33]'
                            }`}
                          >
                            {isApplied ? (
                              <><CheckIcon size={14} /> Applied</>
                            ) : (
                              <>Apply <ArrowIcon size={14} className="jf-arrow" /></>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {/* Apply panel */}
          <aside className="lg:sticky lg:top-6 lg:h-fit">
            <motion.div
              layout
              className="relative overflow-hidden rounded-3xl bg-[#0B1020] text-white shadow-[0_24px_70px_-28px_rgba(11,16,32,0.7)]"
            >
              {/* ambient mesh + texture (persists across states) */}
              <div className="pointer-events-none absolute inset-0">
                <div className="jf-blob-a absolute -right-16 -top-16 h-60 w-60 rounded-full bg-[#5B47E0]/35 blur-[70px]" />
                <div className="jf-blob-b absolute -bottom-14 -left-12 h-56 w-56 rounded-full bg-[#FF6B57]/22 blur-[70px]" />
                <div
                  className="absolute inset-0 opacity-[0.05]"
                  style={{
                    backgroundImage:
                      'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                  }}
                />
              </div>
              <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-400/70 to-transparent" />

              <div className="relative">
                <AnimatePresence mode="wait">
                  {selectedJob ? (
                    <motion.div
                      key={selectedJob.id}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="p-5"
                    >
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-indigo-200 ring-1 ring-white/10">
                          <BoltIcon size={11} /> Apply panel
                        </span>
                        <button
                          onClick={() => setSelectedJob(null)}
                          className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
                          aria-label="Close"
                        >
                          <Ic size={16}><path d="M6 6l12 12M18 6 6 18" /></Ic>
                        </button>
                      </div>

                      <h3 className="mt-3 text-lg font-semibold leading-snug">{selectedJob.title}</h3>
                      <p className="mt-0.5 text-[13px] text-slate-400">{selectedJob.company_name}</p>

                      <div className="mt-4 flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.08] px-2.5 py-1 text-[12px] text-slate-200 ring-1 ring-white/10"><PinIcon size={13} /> {selectedJob.location}</span>
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.08] px-2.5 py-1 text-[12px] text-slate-200 ring-1 ring-white/10"><BagIcon size={13} /> {selectedJob.job_type}</span>
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/[0.08] px-2.5 py-1 text-[12px] text-slate-200 ring-1 ring-white/10"><LevelIcon size={13} /> {selectedJob.experience_level}</span>
                      </div>
                      <p className="mt-2.5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-emerald-300">
                        <CashIcon size={14} /> {formatSalary(selectedJob.salary_min, selectedJob.salary_max)}
                      </p>

                      {/* Apply box */}
                      <div className="mt-4 rounded-2xl bg-white/[0.06] p-4 ring-1 ring-white/10">
                        {!isLoggedIn ? (
                          <>
                            <h4 className="text-sm font-semibold">Login to apply</h4>
                            <p className="mt-1.5 text-[12px] leading-relaxed text-slate-400">
                              Keep browsing freely — sign in only when you're ready to send your application.
                            </p>
                            <motion.button
                              whileTap={{ scale: 0.98 }}
                              onClick={() => navigate('/login')}
                              className="jf-apply mt-3.5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-[13px] font-semibold text-[#0B1020]"
                            >
                              Login to continue <ArrowIcon size={15} className="jf-arrow" />
                            </motion.button>
                          </>
                        ) : resumeLoading ? (
                          <div className="flex items-center gap-2.5 text-[13px] text-slate-300">
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            Checking your resume…
                          </div>
                        ) : resumeProfile?.id ? (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="grid h-7 w-7 place-items-center rounded-lg bg-emerald-400/20 text-emerald-300"><CheckIcon size={15} /></span>
                              <h4 className="text-sm font-semibold">Resume ready</h4>
                            </div>
                            <div className="mt-3 space-y-1 text-[12px] text-slate-300">
                              <p className="truncate">{resumeProfile.original_filename || 'Current resume available'}</p>
                              <p className="truncate text-slate-400">{resumeProfile.extracted_name || resumeProfile.email || 'Details detected'}</p>
                              {resumeProfile.extracted_job_title && <p className="truncate text-slate-400">{resumeProfile.extracted_job_title}</p>}
                            </div>
                            {(resumeProfile.skills || []).length > 0 && (
                              <div className="mt-2.5 flex flex-wrap gap-1.5">
                                {resumeProfile.skills.slice(0, 5).map((skill) => (
                                  <span key={skill} className="rounded-md bg-white/10 px-2 py-0.5 text-[11px] text-slate-200">{skill}</span>
                                ))}
                              </div>
                            )}
                            <div className="mt-4 grid gap-2 sm:grid-cols-2">
                              <motion.button
                                whileTap={{ scale: 0.98 }}
                                onClick={handleApplyJob}
                                disabled={applyLoading || appliedJobs.has(selectedJob.id)}
                                className="jf-apply inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-400 px-3.5 py-2.5 text-[13px] font-semibold text-[#062014] transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {appliedJobs.has(selectedJob.id) ? <><CheckIcon size={14} /> Applied</> : applyLoading ? 'Applying…' : <>Apply now <ArrowIcon size={14} className="jf-arrow" /></>}
                              </motion.button>
                              <label className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg px-3.5 py-2.5 text-[13px] font-semibold text-white ring-1 ring-white/20 transition-colors hover:bg-white/10">
                                <UploadIcon size={14} /> {uploadingResume ? 'Uploading…' : 'Replace'}
                                <input type="file" className="hidden" onChange={handleResumeUpload} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" />
                              </label>
                            </div>
                          </>
                        ) : (
                          <>
                            <h4 className="text-sm font-semibold">Upload a resume to apply</h4>
                            <p className="mt-1.5 text-[12px] leading-relaxed text-slate-400">
                              No current resume on file. Drop one here and apply right after.
                            </p>
                            <label className="jf-apply mt-3.5 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-white px-4 py-2.5 text-[13px] font-semibold text-[#0B1020]">
                              <UploadIcon size={15} /> {uploadingResume ? 'Uploading…' : 'Upload resume'}
                              <input type="file" className="hidden" onChange={handleResumeUpload} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" />
                            </label>
                            <button
                              onClick={() => navigate('/profile')}
                              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-[13px] font-semibold text-white ring-1 ring-white/20 transition-colors hover:bg-white/10"
                            >
                              Open profile
                            </button>
                          </>
                        )}
                      </div>

                      {!!selectedJob.requirements && (
                        <div className="mt-4">
                          <h4 className="text-[13px] font-semibold text-white">Requirements</h4>
                          <p className="mt-1.5 text-[12px] leading-relaxed text-slate-400">{selectedJob.requirements}</p>
                        </div>
                      )}
                      <div className="mt-4">
                        <h4 className="text-[13px] font-semibold text-white">About this role</h4>
                        <p className="mt-1.5 text-[12px] leading-relaxed text-slate-400">{selectedJob.description}</p>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="placeholder"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="p-5"
                    >
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-indigo-200 ring-1 ring-white/10">
                        <BoltIcon size={11} /> How it works
                      </span>
                      <h3 className="mt-3 text-[19px] font-semibold leading-snug">
                        Pick a role to open it here.
                      </h3>
                      <p className="mt-2 text-[13px] leading-relaxed text-slate-400">
                        Company info, role detail, your resume status, and the apply action all share
                        one panel — no jumping between pages.
                      </p>

                      {/* connected stepper — replaces the nested boxes */}
                      <div className="relative mt-6 pl-1">
                        <span className="jf-stepline absolute left-[19px] top-3 bottom-6 w-px bg-gradient-to-b from-indigo-400/70 via-white/25 to-[#FF6B57]/60" />
                        {steps.map((s, i) => (
                          <div key={i} className="relative flex gap-3.5 pb-5 last:pb-0">
                            <span className="relative z-10 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/[0.08] text-indigo-200 ring-1 ring-white/15 backdrop-blur">
                              <s.icon size={16} />
                            </span>
                            <div className="pt-1">
                              <div className="text-[13.5px] font-semibold text-white">{s.title}</div>
                              <div className="mt-0.5 text-[12px] leading-relaxed text-slate-400">{s.text}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-5 flex items-start gap-2.5 border-t border-white/10 pt-4 text-[12px] leading-relaxed text-slate-400">
                        <BuildIcon size={15} className="mt-0.5 shrink-0 text-[#FF9C8E]" />
                        <span>
                          <span className="font-semibold text-slate-200">Hiring?</span> Job posts carry
                          company, salary, experience and skills data out of the box.
                        </span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </aside>
        </div>
      </section>
    </div>
  );
};

/* tiny stable key so the card-reveal effect re-runs only when the visible set changes */
function filteredJobsKey(filters, jobs) {
  return `${jobs.length}|${JSON.stringify(filters)}`;
}

export default JobFeed;